export type PolicyBlock =
  | { type: "text"; content: string }
  | { type: "list"; items: string[] };

export interface PolicySection {
  id: string;
  title: string;
  blocks: PolicyBlock[];
}

export const LAST_UPDATED = "11 August 2026";

export const COMPANY = {
  name: "Sacrum 3D",
  email: "info@sacrum3d.com",
  phone: "+91 8830540638",
  address:
    "35, Om Bunglow, Phatak Baug, Navi Peth, Sadashiv Peth, Pune, Maharashtra 411030",
  cin: "U28299PN2023PTC222338",
  gst: "27ABLCS2758L1ZN",
};

export const privacySections: PolicySection[] = [
  {
    id: "introduction",
    title: "1. Introduction",
    blocks: [
      {
        type: "text",
        content:
          "Sacrum 3D builds custom-fit 3D printed medical casts and related orthopaedic solutions. This Privacy Policy explains what personal data we collect through this website, why we collect it, how we use and protect it, and the choices you have.",
      },
      {
        type: "text",
        content:
          "This policy applies to www.sacrum3d.com and its sub-pages. It does not cover our customer portal at portal.sacrum3d.com, or any third-party website you reach by following a link from here — those are governed by their own notices.",
      },
      {
        type: "text",
        content:
          "By using this website or submitting an enquiry to us, you agree to the practices described below. If you do not agree, please do not use the site.",
      },
    ],
  },
  {
    id: "information-we-collect",
    title: "2. Information We Collect",
    blocks: [
      {
        type: "text",
        content:
          "We collect only what we need to respond to you and to run this website. Specifically:",
      },
      {
        type: "text",
        content:
          "Information you give us. When you fill in our contact or enquiry form, we collect your full name, email address, phone number, the role you select (patient, doctor or partner) and the content of your message. Anything else you choose to tell us in that message is collected too, so please share only what is relevant to your enquiry.",
      },
      {
        type: "text",
        content:
          "Information collected automatically. Like most websites, our hosting infrastructure records standard technical data when you visit — IP address, browser type and version, device and operating system, referring page, and the pages you view along with the date and time. This is used for security and reliability, not to build a profile of you.",
      },
      {
        type: "text",
        content:
          "Information from embedded services. Some pages embed content served by third parties — YouTube videos and Google Forms. When such content loads, those providers may receive your IP address and set their own cookies. See section 6.",
      },
      {
        type: "text",
        content: "We do not knowingly collect the following through this website:",
      },
      {
        type: "list",
        items: [
          "Payment card numbers, bank details or other financial account information.",
          "Government identifiers such as Aadhaar or PAN.",
          "Diagnostic images, scan data, prescriptions or clinical records.",
        ],
      },
    ],
  },
  {
    id: "health-information",
    title: "3. A Note on Health Information",
    blocks: [
      {
        type: "text",
        content:
          "Because we work in medical casting, an enquiry may naturally touch on an injury or medical condition. Health information is sensitive, and we treat anything you send us in that category with strict confidentiality and restricted internal access.",
      },
      {
        type: "text",
        content:
          "Please do not use this website's forms to send detailed clinical records, diagnostic images or anything you would consider confidential medical documentation. If clinical detail is needed for your case, we will arrange a secure channel with you directly. This website is not a medical device, does not provide diagnosis or treatment advice, and is no substitute for consulting a qualified clinician.",
      },
    ],
  },
  {
    id: "how-we-use",
    title: "4. How We Use Your Information",
    blocks: [
      {
        type: "text",
        content: "We use the personal data described above to:",
      },
      {
        type: "list",
        items: [
          "Respond to your enquiry and communicate with you about it.",
          "Assess whether our products are suitable for your requirement, and coordinate a consultation, fitting or demonstration where relevant.",
          "Provide, maintain and improve our products and services.",
          "Operate, secure and troubleshoot this website, and prevent fraud, spam and abuse.",
          "Keep records required for regulatory, tax, accounting and other legal obligations that apply to us in India.",
        ],
      },
      {
        type: "text",
        content:
          "We do not sell your personal data. We do not rent or trade it. We do not use it for automated decision-making that produces legal or similarly significant effects on you, and we will not send you marketing communications unrelated to your enquiry without your consent — you can withdraw that consent at any time.",
      },
    ],
  },
  {
    id: "legal-basis",
    title: "5. Legal Basis for Processing",
    blocks: [
      {
        type: "text",
        content:
          "We are based in India and process personal data in line with the Digital Personal Data Protection Act, 2023 and the rules made under it. We rely on your consent, given when you voluntarily submit an enquiry form, and on the legitimate uses recognised under that Act — including where you have voluntarily provided data for a specified purpose and where processing is necessary to comply with a legal obligation.",
      },
      {
        type: "text",
        content:
          "You may withdraw your consent at any time by writing to us at the address in section 12. Withdrawal does not affect processing already carried out while consent was in force, and may mean we can no longer act on your enquiry.",
      },
    ],
  },
  {
    id: "sharing",
    title: "6. Sharing and Third-Party Services",
    blocks: [
      {
        type: "text",
        content:
          "We share personal data only where it is necessary, and only with the following categories of recipient:",
      },
      {
        type: "list",
        items: [
          "Service providers who process data on our behalf under contract — website and application hosting, email delivery, and IT support. They may use the data only to provide services to us.",
          "Healthcare partners, such as a hospital or clinician, where you have asked us to coordinate your case with them.",
          "Professional advisers — auditors, accountants and lawyers — bound by duties of confidentiality.",
          "Authorities, courts or regulators where disclosure is required by law, or where it is necessary to establish, exercise or defend legal claims, or to protect the safety of any person.",
          "An acquirer or successor entity, if our business is involved in a merger, acquisition or restructuring. We will tell you if your data becomes subject to a materially different privacy policy as a result.",
        ],
      },
      {
        type: "text",
        content:
          "Embedded third-party content. Our pages embed YouTube (operated by Google LLC) to show product and process videos, and Google Forms for certain enquiries. Loading this content lets those providers receive your IP address and set cookies under their own privacy policies, which we do not control. We use YouTube's privacy-enhanced mode where it is available. You can limit this by blocking third-party cookies in your browser, though some content may then not display.",
      },
    ],
  },
  {
    id: "cookies",
    title: "7. Cookies and Similar Technologies",
    blocks: [
      {
        type: "text",
        content:
          "This website does not set advertising or cross-site tracking cookies of its own, and does not run third-party analytics or advertising pixels.",
      },
      {
        type: "text",
        content:
          "Cookies may still be set by the embedded YouTube and Google Forms content described in section 6. Every major browser lets you view, block or delete cookies through its settings; most also offer a private browsing mode. Blocking cookies will not stop you using the informational parts of this site.",
      },
    ],
  },
  {
    id: "retention",
    title: "8. Data Retention",
    blocks: [
      {
        type: "text",
        content:
          "We keep enquiry data only as long as it serves the purpose it was collected for. In practice, enquiries that do not lead to an engagement are erased within three years of our last contact with you, unless you ask us to erase them sooner. Where you become a customer or partner, we retain records for as long as the relationship lasts and afterwards for the period required by applicable tax, company law and product-safety obligations.",
      },
      {
        type: "text",
        content:
          "Server logs are retained for a short operational period and then deleted or aggregated into statistics that cannot identify you.",
      },
    ],
  },
  {
    id: "security",
    title: "9. Security",
    blocks: [
      {
        type: "text",
        content:
          "We use reasonable technical and organisational safeguards to protect personal data — encryption in transit over HTTPS, access controls that limit data to staff who need it, and periodic review of our systems and vendors.",
      },
      {
        type: "text",
        content:
          "No method of transmission over the internet or of electronic storage is completely secure, so we cannot guarantee absolute security. If a personal data breach affects you, we will notify you and the Data Protection Board of India as required by law.",
      },
    ],
  },
  {
    id: "your-rights",
    title: "10. Your Rights",
    blocks: [
      {
        type: "text",
        content:
          "Subject to the conditions in the Digital Personal Data Protection Act, 2023, you have the right to:",
      },
      {
        type: "list",
        items: [
          "Access a summary of the personal data we hold about you and how we process it.",
          "Correct inaccurate data, and complete or update data that is incomplete or out of date.",
          "Erase your personal data where it is no longer needed for the purpose it was collected for and we are not required to keep it.",
          "Withdraw consent you previously gave, at any time.",
          "Nominate another person to exercise these rights on your behalf in the event of your death or incapacity.",
          "Grievance redressal — raise a complaint with us and have it addressed.",
        ],
      },
      {
        type: "text",
        content:
          "To exercise any of these, write to us using the details in section 12. We may ask for information to verify your identity before we act. We aim to respond within 30 days. If you are not satisfied with our response, you may complain to the Data Protection Board of India.",
      },
    ],
  },
  {
    id: "children",
    title: "11. Children's Privacy",
    blocks: [
      {
        type: "text",
        content:
          "This website is intended for adults. We do not knowingly collect personal data directly from children under 18 through this site. Where a cast is being arranged for a child, we expect the enquiry to be submitted by a parent or lawful guardian, who consents on the child's behalf.",
      },
      {
        type: "text",
        content:
          "We do not carry out tracking, behavioural monitoring or targeted advertising directed at children. If you believe a child has provided us personal data without the consent of a parent or guardian, contact us and we will delete it.",
      },
    ],
  },
  {
    id: "contact",
    title: "12. Contact Us",
    blocks: [
      {
        type: "text",
        content:
          "For any question about this policy, to exercise your rights, or to raise a grievance about how we handle your personal data, reach out to us and we will respond as quickly as we can.",
      },
      {
        type: "list",
        items: [
          `Email: ${COMPANY.email}`,
          `Phone: ${COMPANY.phone}`,
          `Address: ${COMPANY.address}`,
          `CIN: ${COMPANY.cin}`,
          `GST No.: ${COMPANY.gst}`,
        ],
      },
    ],
  },
  {
    id: "changes",
    title: "13. Changes to This Policy",
    blocks: [
      {
        type: "text",
        content:
          "We may update this Privacy Policy from time to time to reflect changes in our practices or in the law. When we do, we will revise the \"Last updated\" date at the top of this page. If the changes are significant, we will provide a more prominent notice. Please review this page periodically.",
      },
    ],
  },
];
